import firebase from 'firebase/app';
import 'firebase/database';

// Per-tenant Slack settings live in the tenant's own database
export const SLACK_CONFIG_PATH = 'integrations/slack';
const SLACK_WEBHOOK_PATTERN = /^https:\/\/hooks\.slack\.com\/services\/\S+$/;

export function isValidSlackWebhook(url) {
  return SLACK_WEBHOOK_PATTERN.test(url || '');
}

export async function getSlackConfig() {
  const snapshot = await firebase.database().ref(SLACK_CONFIG_PATH).once('value');
  return snapshot.val() || {};
}

export async function saveSlackConfig(config) {
  await firebase.database().ref(SLACK_CONFIG_PATH).set(config);
}

export async function removeSlackConfig() {
  await firebase.database().ref(SLACK_CONFIG_PATH).remove();
}

// Slack webhooks don't send CORS headers, so post as a "simple" form request.
// The response is opaque: a resolved promise means the request was sent, not that Slack accepted it.
export function postToSlack(webhookUrl, message) {
  const body = new URLSearchParams({ payload: JSON.stringify(message) });
  return fetch(webhookUrl, { method: 'POST', mode: 'no-cors', body });
}

function truncate(text, max = 2900) {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export function formatActivityForSlack(data) {
  const fields = [
    ['By', data.actionUsername || data.activityByUserName],
    ['Affected user', data.activityOnUserName],
    ['Type', data.type],
    ['Time', data.timestamp],
  ]
    .filter(([, value]) => value)
    .map(([label, value]) => ({ type: 'mrkdwn', text: `*${label}*\n${value}` }));

  const blocks = [
    { type: 'header', text: { type: 'plain_text', text: truncate(data.activityName || 'Activity', 150) } },
  ];
  if (data.activityDescription) {
    blocks.push({ type: 'section', text: { type: 'mrkdwn', text: truncate(data.activityDescription) } });
  }
  if (fields.length) {
    blocks.push({ type: 'section', fields });
  }
  if (data.changes) {
    blocks.push({ type: 'section', text: { type: 'mrkdwn', text: truncate(`*Changes*\n\`\`\`${data.changes}\`\`\``) } });
  }
  if (data.change) {
    const lines = Object.entries(data.change).map(([key, value]) => `• *${key}*: ${value}`).join('\n');
    blocks.push({ type: 'section', text: { type: 'mrkdwn', text: truncate(`*Change*\n${lines}`) } });
  }
  if (data.reason) {
    blocks.push({ type: 'section', text: { type: 'mrkdwn', text: truncate(`*Reason*\n${data.reason}`) } });
  }
  if (data.attendees) {
    blocks.push({ type: 'section', text: { type: 'mrkdwn', text: truncate(`*Attendees*\n${data.attendees}`) } });
  }
  const context = [data.osName, data.map ? `<${data.map}|View location>` : null].filter(Boolean);
  if (context.length) {
    blocks.push({ type: 'context', elements: [{ type: 'mrkdwn', text: context.join('  •  ') }] });
  }

  return { text: data.activityName || 'New activity', blocks };
}

// Never throws: a Slack problem must not block the action being logged
export async function notifySlackOfActivity(activityData) {
  try {
    const config = await getSlackConfig();
    if (!config.enabled || !isValidSlackWebhook(config.webhookUrl)) return;
    await postToSlack(config.webhookUrl, formatActivityForSlack(activityData));
  } catch (error) {
    console.warn('Slack notification failed:', error);
  }
}



// WEBPACK FOOTER //
// ./src/utils/slack.js